import styles from './styles.scss';
import cn from 'classnames';
import { NO_ELEMENT } from 'utils';
import { isCheckoutPage } from 'models/CheckoutPageModel';
import { useSelector } from 'react-redux';
import { PageState } from 'models/PageState';
import { AlertNotification as AlertNotificationModel, DisplayType } from 'models/AlertNotification';
import { getChannel } from 'views/ProductPage/selector';
import { Wrap } from 'components/Wrap';
import { forwardRef, HTMLAttributes } from 'react';

interface AlertProps extends AlertNotificationModel {
  className?: string;
  children?: React.ReactNode;
  isDisabled?: boolean;
}

const useOld = () => {
  const channel = useSelector(getChannel);
  return channel === 'GOP';
};

const SitewideAlertContainer = ({ children }: Pick<AlertProps, 'children'>) => {
  const isOld = useOld();

  return (
    <div
      className={cn({
        [styles.smallPadding]: isOld,
        [styles.largePadding]: !isOld
      })}
    >
      {children}
    </div>
  );
};

const AlertIcon = () => (
  <div className={styles.iconContainer}>
    <div className={styles.icon}>!</div>
  </div>
);

export const AlertContainer = forwardRef<
  HTMLDivElement,
  Omit<AlertProps, 'message'> & HTMLAttributes<HTMLDivElement>
>(({ className, children, displayType, url, isDisabled, ...attr }, ref) => (
  <Wrap when={url} wrapper={<a href={url} className={styles.link} />}>
    <div
      ref={ref}
      {...attr}
      className={cn(
        styles.container,
        {
          [styles.critical]: displayType === DisplayType.Critical,
          [styles.information]: displayType === DisplayType.Information,
          [styles.warning]: displayType === DisplayType.Warning,
          [styles.success]: displayType === DisplayType.Success,
          [styles.blackFriday]: displayType === DisplayType.Blackfriday,
          [styles.disabled]: isDisabled
        },
        className
      )}
    >
      {children}
    </div>
  </Wrap>
));

export const AlertNotification = (alert: Omit<AlertProps, 'children'>) => {
  const isCheckout = useSelector(({ currentPage }: PageState) => isCheckoutPage(currentPage));
  const isOld = useOld();
  const displaySmall = isCheckout || isOld;

  if (!alert.message) {
    return NO_ELEMENT;
  }

  const { displayType = DisplayType.Information, message, url, isDisabled } = alert;

  return (
    <div
      className={cn(styles.content, {
        [styles.large]: !displaySmall,
        [styles.small]: displaySmall,
        [styles.checkout]: displaySmall
      })}
    >
      <AlertContainer displayType={displayType} url={url} isDisabled={isDisabled}>
        <AlertIcon />
        <p className={styles.text}>{message}</p>
      </AlertContainer>
    </div>
  );
};

interface AlertNotificationsProps {
  alerts: AlertNotificationModel[];
  isDisabled?: boolean;
}
export const AlertNotifications = ({ alerts, isDisabled }: AlertNotificationsProps) => {
  return (
    <>
      {alerts.map((alert, index) => (
        <AlertNotification key={index} {...alert} isDisabled={isDisabled} />
      ))}
    </>
  );
};

export const SitewideAlertNotifications = ({ alerts, isDisabled }: AlertNotificationsProps) => {
  return (
    <SitewideAlertContainer>
      <AlertNotifications alerts={alerts} isDisabled={isDisabled} />
    </SitewideAlertContainer>
  );
};
